import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import Layout from './components/layout/Layout';
import Loading from './components/common/Loading';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const LevelsPage = lazy(() => import('./pages/LevelsPage'));
const LevelDetailPage = lazy(() => import('./pages/LevelDetailPage'));
const PracticePage = lazy(() => import('./pages/PracticePage'));
const WritingPracticePage = lazy(() => import('./pages/WritingPracticePage'));
const ListeningPracticePage = lazy(() => import('./pages/ListeningPracticePage'));
const SpeakingPracticePage = lazy(() => import('./pages/SpeakingPracticePage'));
const AISpeakingPracticePage = lazy(() => import('./pages/AISpeakingPracticePage'));
const ExamsPage = lazy(() => import('./pages/ExamsPage'));
const ExamPage = lazy(() => import('./pages/ExamPage'));
const ExamResultPage = lazy(() => import('./pages/ExamResultPage'));
const VocabularyPage = lazy(() => import('./pages/VocabularyPage'));
const FlashcardsPage = lazy(() => import('./pages/FlashcardsPage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const ParentDashboardPage = lazy(() => import('./pages/ParentDashboardPage'));
const TeacherDashboardPage = lazy(() => import('./pages/TeacherDashboardPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
        <Route path="/register" element={<GuestRoute><RegisterPage /></GuestRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/levels" element={<ProtectedRoute><LevelsPage /></ProtectedRoute>} />
        <Route path="/levels/:code" element={<ProtectedRoute><LevelDetailPage /></ProtectedRoute>} />
        <Route path="/practice/:level/READING" element={<ProtectedRoute><PracticePage /></ProtectedRoute>} />
        <Route path="/practice/:level/WRITING" element={<ProtectedRoute><WritingPracticePage /></ProtectedRoute>} />
        <Route path="/practice/:level/LISTENING" element={<ProtectedRoute><ListeningPracticePage /></ProtectedRoute>} />
        <Route path="/practice/:level/SPEAKING" element={<ProtectedRoute><SpeakingPracticePage /></ProtectedRoute>} />
        <Route path="/practice/:level/SPEAKING/ai" element={<ProtectedRoute><AISpeakingPracticePage /></ProtectedRoute>} />
        <Route path="/exams" element={<ProtectedRoute><ExamsPage /></ProtectedRoute>} />
        <Route path="/exams/:id" element={<ProtectedRoute><ExamPage /></ProtectedRoute>} />
        <Route path="/exams/:id/result" element={<ProtectedRoute><ExamResultPage /></ProtectedRoute>} />
        <Route path="/vocabulary" element={<ProtectedRoute><VocabularyPage /></ProtectedRoute>} />
        <Route path="/flashcards" element={<ProtectedRoute><FlashcardsPage /></ProtectedRoute>} />
        <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/pricing" element={<ProtectedRoute><PricingPage /></ProtectedRoute>} />
        <Route path="/parent-dashboard" element={<ProtectedRoute><ParentDashboardPage /></ProtectedRoute>} />
        <Route path="/teacher-dashboard" element={<ProtectedRoute><TeacherDashboardPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
